"use client"
import { Button } from "@/components/ui/Button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Edit3 } from "lucide-react"
import React, { useCallback, useEffect, useState } from "react"

type EntityKind = "node" | "term"

interface DrupalEntityEditButtonProps {
  entity: EntityKind
  /** Numeric internal id (nid for node, tid for term). Preferred when available. */
  internalId?: number | string
  /** Optional override to pass a complete edit URL directly. */
  urlOverride?: string
  /** Tooltip message. */
  message?: string
  alwaysShow?: boolean
}

function getDrupalBaseUrl(): string {
  const base = (
    process.env.NEXT_PUBLIC_SOLUTIONS_BACKEND_BASE_URL ||
    process.env.DRUPAL_URL ||
    ""
  ).trim()
  return base.replace(/\/$/, "")
}

function buildEditUrl(params: {
  entity: EntityKind
  internalId?: number | string
  urlOverride?: string
}): string | null {
  const { entity, internalId, urlOverride } = params
  if (urlOverride) return urlOverride
  const base = getDrupalBaseUrl()
  if (!base) return null
  if (!internalId) return null
  if (entity === "node") return `${base}/node/${internalId}/edit`
  if (entity === "term") return `${base}/taxonomy/term/${internalId}/edit`
  return null
}

// Reuse the same cookie-driven toggle as EditButton
const useEditMode = () => {
  const [isEditModeVisible, setIsEditModeVisible] = useState(false)

  useEffect(() => {
    const editModeCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("editMode="))
      ?.split("=")[1]
    setIsEditModeVisible(editModeCookie === "true")

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === "a") {
        event.preventDefault()
        setIsEditModeVisible((currentState) => {
          const newState = !currentState
          const expires = new Date()
          expires.setDate(expires.getDate() + 30)
          document.cookie = `editMode=${newState}; expires=${expires.toUTCString()}; path=/`
          return newState
        })
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  return isEditModeVisible
}

const DrupalEntityEditButton: React.FC<DrupalEntityEditButtonProps> = ({
  entity,
  internalId,
  urlOverride,
  message,
  alwaysShow = false,
}) => {
  const isEditModeVisible = useEditMode()
  const url = buildEditUrl({ entity, internalId, urlOverride })

  const handleClick = useCallback(() => {
    if (url) window.open(url, "_blank")
  }, [url])

  if ((!isEditModeVisible || !url) && !alwaysShow) return null

  return (
    <Tooltip key={`${url}`} delayDuration={300}>
      <TooltipTrigger asChild>
        <Button
          onClick={handleClick}
          variant="default"
          size="icon"
          aria-label="Edit"
          name="Edit"
          className="rounded-full"
        >
          <Edit3 />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-sm">
        {message ? <strong>{message}</strong> : <strong>Edit in Drupal</strong>}
        <div className="text-xs">{url}</div>
      </TooltipContent>
    </Tooltip>
  )
}

export default DrupalEntityEditButton
